import React, { useState, useEffect, useCallback } from "react";
import { RootState, useAppThunkDispatch } from "redux/store";
import { useSelector } from "react-redux";
import { getUser, getAdminMode, getMobileDialogDate, getMobileDialogClickLogin, getAgentById } from "redux/selector";
import { resetMobileDialogDate, setClickMobileLogin } from "redux/slice/mobile-dialog-date";
import moment from "moment";
import * as Mui from "@mui/material";
import { updateSplashScreen } from "api/api";

interface Props {
}

const Component: React.FC<Props> = () => {
	const dispatch = useAppThunkDispatch();
	
	// Redux selectors
	const user = useSelector((state: RootState) => getUser(state));
	const adminMode = useSelector((state: RootState) => getAdminMode(state));
	const mobileDialogDate = useSelector((state: RootState) => getMobileDialogDate(state));
	const mobileDialogClickLogin = useSelector((state: RootState) => getMobileDialogClickLogin(state));
	const agent = useSelector((state: RootState) => getAgentById(state, user.agentId));

	// Local state
	const [open, setOpen] = useState<boolean | undefined>(undefined);
	const [loaded, setLoaded] = useState(false);

	// Check if dialog should be shown
	useEffect(() => {
		if (
			open === undefined
			&& adminMode === false
			&& !user.boardIds.includes(251)
			&& !user.websitePlatform.wix
			&& user.permissions.mobileApp
			&& !user.lastMobileLogin
			&& !mobileDialogDate && !user.splashScreenSkipDate
		) {
			setOpen(true);
		}
	}, [open, adminMode, user.boardIds, user.websitePlatform.wix, user.permissions.mobileApp, user.lastMobileLogin, mobileDialogDate, user.splashScreenSkipDate]);

	// Callback functions
	const handleClose = useCallback(() => {
		setOpen(false);
		dispatch(resetMobileDialogDate(moment()));
	}, [dispatch]);

	const handleSplashScreenUpdate = useCallback(() => {
		const agentPayload = {
			id: user.agentId,
			splashScreenSkipDate: moment().toISOString(),
		};
		updateSplashScreen(agentPayload);
	}, [user.agentId]);

	const handleSkipClick = useCallback(() => {
		handleClose();
		handleSplashScreenUpdate();
	}, [handleClose, handleSplashScreenUpdate]);

	const handleLoggedInClick = useCallback(() => {
		handleClose();
		if (!user.lastMobileLogin && !user.splashScreenSkipDate) {
			handleSplashScreenUpdate();
		}
	}, [handleClose, user.lastMobileLogin, user.splashScreenSkipDate, handleSplashScreenUpdate]);

	const handleIframeLoad = useCallback(() => {
		setLoaded(true);
	}, []);

	const handleIframeError = useCallback(() => {
		handleClose();
	}, [handleClose]);

	// TODO: Re-enable once https://www.ihomefinder.com/landing/control-panel-app-splash-page/ is fixed (displaying raw WordPress shortcodes)
	const splashPageDisabled = true;
	if (splashPageDisabled) {
		return null;
	}

	if (!open) {
		return null;
	}

	return (
		<Mui.Dialog
			open={true}
			fullScreen
		>
			<Mui.DialogContent
				style={{
					display: "flex",
					padding: 0,
					backgroundColor: "#36747B",
				}}>
				<iframe
					style={{
						width: "100%",
					}}
					onLoad={handleIframeLoad}
					onError={handleIframeError}
					src="https://www.ihomefinder.com/landing/control-panel-app-splash-page/"
				></iframe>
			</Mui.DialogContent>
			<Mui.DialogActions
				style={{
					height: 0,
					padding: 0,
					justifyContent: "center",
				}}
			>
				<div
					style={{
						bottom: 50,
						position: "absolute",
					}}
				>
					<Mui.Button
						variant="contained"
						color="primary"
						style={{
							textTransform: "revert",
							marginRight: 10,
							backgroundColor: "#fb7434",
							color: "#000",
							fontWeight: "bold",
						}}
						onClick={handleLoggedInClick}
					>
						I've logged into the app
					</Mui.Button>
					<Mui.Button
						variant="contained"
						style={{
							textTransform: "revert",
						}}
						onClick={handleSkipClick}
					>
						Skip for now
					</Mui.Button>
				</div>
			</Mui.DialogActions>
		</Mui.Dialog>
	);
};

export const MobileAppDialog = Component;
